/**
 * Facebook Pixel - Lazy Loaded
 *
 * This module defers Facebook Pixel loading until user interaction or browser idle,
 * removing it from the critical rendering path for better Lighthouse scores.
 *
 * Why lazy load?
 * - FB Pixel adds ~1s to LCP and 100-400ms to TBT when loaded immediately
 * - The Privacy Sandbox Topics API registration request can block network idle
 * - Marketing sites prioritize performance over tracking every bounce
 *
 * Strategy: 'interaction-or-idle'
 * - Loads on first user interaction (click, scroll, keydown, touchstart)
 * - OR after requestIdleCallback with 4s timeout (after window.load)
 * - Whichever comes first
 *
 * Trade-offs:
 * - ✅ Removes FB Pixel from critical path
 * - ✅ Better Lighthouse scores (LCP, TBT)
 * - ⚠️ May miss some bounces (users who leave < 4s without interaction)
 * - ⚠️ PageView event fires later than traditional implementation
 *
 * @see https://developers.facebook.com/docs/meta-pixel/
 */

const FB_PIXEL_ID = '334458340518223';
const FB_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';

let loading = false;
let loaded = false;

// Type alias referencing the global declaration in global.d.ts
type FbqFunction = NonNullable<typeof window.fbq>;

/**
 * Install the fbq stub that queues calls until the real script loads.
 * This allows fbq() calls to work even before the script is loaded.
 */
function ensureFbqStub(): void {
  if (typeof window === 'undefined') return;
  if (window.fbq) return;

  // Create the queue array first
  const queue: unknown[] = [];

  // Create the fbq function with all required properties
  const fbq: FbqFunction = Object.assign(
    function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        queue.push(args);
      }
    },
    {
      queue,
      loaded: false,
      version: '2.0',
      push: null as unknown as FbqFunction, // Will be set below
      callMethod: undefined,
    }
  );

  // Self-reference for push
  fbq.push = fbq;

  window.fbq = fbq;
  window._fbq = fbq;
}

/**
 * Inject the Facebook Pixel script into the document.
 */
function loadFbScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById('fb-pixel-script')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'fb-pixel-script';
    script.async = true;
    script.src = FB_SCRIPT_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Facebook Pixel failed to load'));
    document.head.appendChild(script);
  });
}

/**
 * Initialize the pixel after the script loads.
 */
function initPixel(): void {
  if (loaded || loading) return;
  loading = true;

  ensureFbqStub();

  loadFbScript()
    .then(() => {
      if (!window.fbq) return;

      window.fbq('init', FB_PIXEL_ID);
      window.fbq('track', 'PageView');
      loaded = true;

      // eslint-disable-next-line no-console
      console.debug('[Facebook Pixel] Initialized (lazy loaded)');
    })
    .catch(error => {
      // Non-blocking - pixel may be blocked by ad blockers
      // eslint-disable-next-line no-console
      console.debug('[Facebook Pixel] Failed to load:', error);
    });
}

type Strategy = 'interaction' | 'idle' | 'interaction-or-idle';

interface FacebookPixelOptions {
  /** Loading strategy (default: 'interaction-or-idle') */
  strategy?: Strategy;
  /** Idle timeout in ms (default: 4000) */
  idleTimeoutMs?: number;
}

/**
 * Initialize Facebook Pixel with lazy loading.
 *
 * @param options Configuration options
 * @param options.strategy Loading strategy:
 *   - 'interaction': Only load on user interaction (best for Lighthouse)
 *   - 'idle': Only load on requestIdleCallback (may still delay Lighthouse)
 *   - 'interaction-or-idle': Load on first interaction OR idle (balanced)
 * @param options.idleTimeoutMs Timeout for idle callback (default: 4000ms)
 */
export function initFacebookPixel(options?: FacebookPixelOptions): void {
  if (typeof window === 'undefined') return;

  const strategy = options?.strategy ?? 'interaction-or-idle';
  const idleTimeoutMs = options?.idleTimeoutMs ?? 4000;

  // Install stub immediately so fbq() calls are queued
  ensureFbqStub();

  const trigger = () => initPixel();

  // Set up idle trigger (after window.load for better timing)
  if (strategy !== 'interaction') {
    const setupIdleTrigger = () => {
      if ('requestIdleCallback' in window) {
        (
          window as Window & {
            requestIdleCallback: (cb: IdleRequestCallback, opts?: { timeout: number }) => number;
          }
        ).requestIdleCallback(() => trigger(), { timeout: idleTimeoutMs });
      } else {
        // Safari fallback
        setTimeout(trigger, idleTimeoutMs);
      }
    };

    // Wait for window.load before scheduling idle callback
    if (document.readyState === 'complete') {
      setupIdleTrigger();
    } else {
      window.addEventListener('load', setupIdleTrigger, { once: true });
    }
  }

  // Set up interaction triggers
  if (strategy !== 'idle') {
    const onInteract = () => {
      trigger();
      // Clean up all listeners after first interaction
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
      window.removeEventListener('scroll', onInteract);
      window.removeEventListener('touchstart', onInteract);
    };

    window.addEventListener('pointerdown', onInteract, { passive: true });
    window.addEventListener('keydown', onInteract, { passive: true });
    window.addEventListener('scroll', onInteract, { passive: true });
    window.addEventListener('touchstart', onInteract, { passive: true });
  }
}

/**
 * Check if Facebook Pixel is loaded.
 */
export function isFacebookPixelLoaded(): boolean {
  return loaded;
}

/**
 * Manually trigger pixel load (for testing or specific use cases).
 */
export function loadFacebookPixelNow(): void {
  initPixel();
}
